/**
 * Marketing Permissions Service
 * Handles RBAC permission checking for marketing management features
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export interface MarketingPermissions {
  MARKETING_CONTENT_VIEW: boolean;
  MARKETING_CONTENT_EDIT: boolean;
  MARKETING_CONTENT_DELETE: boolean;
  MARKETING_ABTEST_MANAGE: boolean;
  MARKETING_ANALYTICS_VIEW: boolean;
  MARKETING_CACHE_MANAGE: boolean;
  MARKETING_APPROVAL_WORKFLOW: boolean;
}

export interface PermissionCheckResult {
  hasPermission: boolean;
  reason?: string;
  requiredRole?: string;
  auditId?: string;
}

export interface AuditLogEntry {
  event_type: 'permission_check' | 'access_granted' | 'access_denied';
  user_id: string;
  resource_id: string;
  resource_type: string;
  action_attempted: string;
  permission_result: 'granted' | 'denied';
  decision_basis: string;
  risk_score: number;
  compliance_flags: string[];
}

class MarketingPermissionsService {
  private permissionCache = new Map<string, { permissions: MarketingPermissions; expires: number }>();
  private cacheExpiry = 5 * 60 * 1000; // 5 minutes

  async checkMarketingPermissions(userId: string): Promise<MarketingPermissions> {
    const cacheKey = `marketing_permissions_${userId}`;
    const cached = this.permissionCache.get(cacheKey);

    if (cached && cached.expires > Date.now()) {
      return cached.permissions;
    }

    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const response = await axios.post(
        `${API_BASE_URL}/v1/rbac/check`,
        {
          user_id: userId,
          permissions: [
            'MARKETING_CONTENT_VIEW',
            'MARKETING_CONTENT_EDIT',
            'MARKETING_CONTENT_DELETE',
            'MARKETING_ABTEST_MANAGE',
            'MARKETING_ANALYTICS_VIEW',
            'MARKETING_CACHE_MANAGE',
            'MARKETING_APPROVAL_WORKFLOW',
          ],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const permissions: MarketingPermissions = {
          MARKETING_CONTENT_VIEW: response.data.data.permissions.includes('MARKETING_CONTENT_VIEW'),
          MARKETING_CONTENT_EDIT: response.data.data.permissions.includes('MARKETING_CONTENT_EDIT'),
          MARKETING_CONTENT_DELETE: response.data.data.permissions.includes('MARKETING_CONTENT_DELETE'),
          MARKETING_ABTEST_MANAGE: response.data.data.permissions.includes('MARKETING_ABTEST_MANAGE'),
          MARKETING_ANALYTICS_VIEW: response.data.data.permissions.includes('MARKETING_ANALYTICS_VIEW'),
          MARKETING_CACHE_MANAGE: response.data.data.permissions.includes('MARKETING_CACHE_MANAGE'),
          MARKETING_APPROVAL_WORKFLOW: response.data.data.permissions.includes('MARKETING_APPROVAL_WORKFLOW'),
        };

        // Cache the result
        this.permissionCache.set(cacheKey, {
          permissions,
          expires: Date.now() + this.cacheExpiry,
        });

        return permissions;
      }
    } catch (error) {
      console.error('Failed to check marketing permissions:', error);
    }

    // Default to no permissions on error
    return {
      MARKETING_CONTENT_VIEW: false,
      MARKETING_CONTENT_EDIT: false,
      MARKETING_CONTENT_DELETE: false,
      MARKETING_ABTEST_MANAGE: false,
      MARKETING_ANALYTICS_VIEW: false,
      MARKETING_CACHE_MANAGE: false,
      MARKETING_APPROVAL_WORKFLOW: false,
    };
  }

  async checkSpecificPermission(
    userId: string,
    permission: keyof MarketingPermissions
  ): Promise<PermissionCheckResult> {
    try {
      const permissions = await this.checkMarketingPermissions(userId);
      const hasPermission = permissions[permission];

      // Log the permission check
      await this.logPermissionCheck(userId, permission, hasPermission ? 'granted' : 'denied');

      if (!hasPermission) {
        return {
          hasPermission: false,
          reason: `User does not have ${permission} permission`,
          requiredRole: this.getRequiredRoleForPermission(permission),
        };
      }

      return {
        hasPermission: true,
      };
    } catch (error) {
      return {
        hasPermission: false,
        reason: 'Permission check failed due to system error',
      };
    }
  }

  async validateMarketingPortalAccess(userId: string): Promise<PermissionCheckResult> {
    return await this.checkSpecificPermission(userId, 'MARKETING_CONTENT_VIEW');
  }

  async validateContentEditing(userId: string): Promise<PermissionCheckResult> {
    return await this.checkSpecificPermission(userId, 'MARKETING_CONTENT_EDIT');
  }

  async validateContentDeletion(userId: string): Promise<PermissionCheckResult> {
    return await this.checkSpecificPermission(userId, 'MARKETING_CONTENT_DELETE');
  }

  async validateABTestManagement(userId: string): Promise<PermissionCheckResult> {
    return await this.checkSpecificPermission(userId, 'MARKETING_ABTEST_MANAGE');
  }

  async validateAnalyticsAccess(userId: string): Promise<PermissionCheckResult> {
    return await this.checkSpecificPermission(userId, 'MARKETING_ANALYTICS_VIEW');
  }

  async validateCacheManagement(userId: string): Promise<PermissionCheckResult> {
    return await this.checkSpecificPermission(userId, 'MARKETING_CACHE_MANAGE');
  }

  async validateApprovalWorkflow(userId: string): Promise<PermissionCheckResult> {
    return await this.checkSpecificPermission(userId, 'MARKETING_APPROVAL_WORKFLOW');
  }

  private async logPermissionCheck(userId: string, permission: string, result: 'granted' | 'denied') {
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      await axios.post(
        `${API_BASE_URL}/v1/rbac/audit`,
        {
          event_type: result === 'granted' ? 'access_granted' : 'access_denied',
          user_id: userId,
          resource_type: 'marketing_portal',
          action_attempted: permission,
          permission_result: result,
          decision_basis: JSON.stringify({
            permission_checked: permission,
            timestamp: new Date().toISOString(),
            source: 'marketing_permissions_service',
          }),
          risk_score: this.calculateRiskScore(permission, result),
          compliance_flags: ['FERPA', 'COPPA', 'MARKETING_AUDIT'],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      console.error('Failed to log permission check:', error);
    }
  }

  private getRequiredRoleForPermission(permission: keyof MarketingPermissions): string {
    const roleRequirements: Record<keyof MarketingPermissions, string> = {
      MARKETING_CONTENT_VIEW: 'admin or marketing_manager',
      MARKETING_CONTENT_EDIT: 'admin or marketing_manager',
      MARKETING_CONTENT_DELETE: 'admin only',
      MARKETING_ABTEST_MANAGE: 'admin or marketing_manager',
      MARKETING_ANALYTICS_VIEW: 'admin or marketing_manager',
      MARKETING_CACHE_MANAGE: 'admin only',
      MARKETING_APPROVAL_WORKFLOW: 'admin or marketing_manager',
    };

    return roleRequirements[permission];
  }

  private calculateRiskScore(permission: string, result: string): number {
    const riskLevels: Record<string, number> = {
      MARKETING_CONTENT_VIEW: 10,
      MARKETING_CONTENT_EDIT: 30,
      MARKETING_CONTENT_DELETE: 80,
      MARKETING_ABTEST_MANAGE: 40,
      MARKETING_ANALYTICS_VIEW: 20,
      MARKETING_CACHE_MANAGE: 70,
      MARKETING_APPROVAL_WORKFLOW: 50,
    };

    const baseRisk = riskLevels[permission] || 50;
    return result === 'denied' ? Math.min(baseRisk + 20, 100) : baseRisk;
  }

  clearPermissionCache(userId?: string) {
    if (userId) {
      this.permissionCache.delete(`marketing_permissions_${userId}`);
    } else {
      this.permissionCache.clear();
    }
  }

  // Utility function to check if user has any marketing access
  async hasAnyMarketingAccess(userId: string): Promise<boolean> {
    const permissions = await this.checkMarketingPermissions(userId);
    return Object.values(permissions).some(permission => permission === true);
  }

  // Utility function to get user's marketing permission summary
  async getMarketingPermissionSummary(userId: string): Promise<{
    hasAccess: boolean;
    permissions: MarketingPermissions;
    accessLevel: 'none' | 'view_only' | 'content_manager' | 'full_admin';
    restrictions: string[];
  }> {
    const permissions = await this.checkMarketingPermissions(userId);
    const hasAccess = await this.hasAnyMarketingAccess(userId);

    let accessLevel: 'none' | 'view_only' | 'content_manager' | 'full_admin' = 'none';
    const restrictions: string[] = [];

    if (!hasAccess) {
      accessLevel = 'none';
      restrictions.push('No marketing access permissions');
    } else if (permissions.MARKETING_CONTENT_DELETE && permissions.MARKETING_CACHE_MANAGE) {
      accessLevel = 'full_admin';
    } else if (permissions.MARKETING_CONTENT_EDIT) {
      accessLevel = 'content_manager';
      if (!permissions.MARKETING_CONTENT_DELETE) {
        restrictions.push('Cannot delete content');
      }
      if (!permissions.MARKETING_CACHE_MANAGE) {
        restrictions.push('Cannot manage cache');
      }
    } else if (permissions.MARKETING_CONTENT_VIEW) {
      accessLevel = 'view_only';
      restrictions.push('Read-only access');
    }

    return {
      hasAccess,
      permissions,
      accessLevel,
      restrictions,
    };
  }
}

export const marketingPermissionsService = new MarketingPermissionsService();
export default marketingPermissionsService;
